/**
 * Minimal, dependency-free JWT (HS256) — our own auth, no third-party SDK.
 *
 * Uses the Web Crypto API (available in Node, browsers, and Workers), so the
 * same code signs on our API server and verifies anywhere. Tokens are standard
 * HS256 JWTs, so nothing here is vendor-specific. The signing secret lives only
 * on the server; the browser receives a token and sends it back as a bearer.
 */

export interface JwtClaims {
  /** Subject — the owner id. */
  readonly sub: string;
  /** Issued-at (epoch seconds). */
  readonly iat: number;
  /** Expiry (epoch seconds). */
  readonly exp: number;
  readonly [key: string]: unknown;
}

export type JwtFailure = 'malformed' | 'bad_signature' | 'expired';

export type JwtVerifyResult =
  | { readonly valid: true; readonly claims: JwtClaims }
  | { readonly valid: false; readonly reason: JwtFailure; readonly claims?: JwtClaims };

const enc = new TextEncoder();
const dec = new TextDecoder();

function b64urlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlToBytes(s: string): Uint8Array {
  let t = s.replace(/-/g, '+').replace(/_/g, '/');
  while (t.length % 4) t += '=';
  const bin = atob(t);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function hmacKey(secret: string) {
  return crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign', 'verify']);
}

/** Sign claims into an HS256 JWT. */
export async function signJwt(claims: JwtClaims, secret: string): Promise<string> {
  const header = b64urlEncode(enc.encode(JSON.stringify({ alg: 'HS256', typ: 'JWT' })));
  const payload = b64urlEncode(enc.encode(JSON.stringify(claims)));
  const data = `${header}.${payload}`;
  const key = await hmacKey(secret);
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(data)));
  return `${data}.${b64urlEncode(sig)}`;
}

/** Verify an HS256 JWT: signature, structure, and expiry. */
export async function verifyJwt(
  token: string,
  secret: string,
  nowSec: number = Math.floor(Date.now() / 1000),
): Promise<JwtVerifyResult> {
  const parts = token.split('.');
  if (parts.length !== 3) return { valid: false, reason: 'malformed' };
  const [header, payload, sig] = parts as [string, string, string];
  const key = await hmacKey(secret);
  let signatureOk: boolean;
  try {
    signatureOk = await crypto.subtle.verify('HMAC', key, b64urlToBytes(sig), enc.encode(`${header}.${payload}`));
  } catch {
    return { valid: false, reason: 'malformed' };
  }
  if (!signatureOk) return { valid: false, reason: 'bad_signature' };

  let claims: JwtClaims;
  try {
    claims = JSON.parse(dec.decode(b64urlToBytes(payload))) as JwtClaims;
  } catch {
    return { valid: false, reason: 'malformed' };
  }
  if (typeof claims.exp === 'number' && nowSec > claims.exp) {
    return { valid: false, reason: 'expired', claims };
  }
  return { valid: true, claims };
}

/** Build standard claims for an owner session. Pure. */
export function ownerClaims(ownerId: string, nowSec: number, ttlSec: number): JwtClaims {
  return { sub: ownerId, iat: nowSec, exp: nowSec + ttlSec };
}
